import {
  getISOWeek,
  isWeekend,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
} from "date-fns";
import { isRedDay } from "@/utils/date";

export type WeekGroupDay = {
  date: Date;
  isWeekend: boolean;
  isRedDay: boolean;
};

export type WeekGroup = {
  weekNumber: number;
  startDate: Date;
  days: WeekGroupDay[];
  padLeft: number;
  padRight: number;
};

/**
 * Build week groups from an array of days.
 * Always includes weekend days with isWeekend flag for placeholder rendering.
 * Padding is calculated for a full 7-day week structure.
 */
export function buildWeekGroups(allDays: Date[]): WeekGroup[] {
  const groups: Map<number, Date[]> = new Map();

  allDays.forEach((day) => {
    const weekNum = getISOWeek(day);
    const existing = groups.get(weekNum) || [];
    groups.set(weekNum, [...existing, day]);
  });

  return Array.from(groups.entries()).map(([weekNumber, days]) => {
    const firstDay = days[0];
    const lastDay = days[days.length - 1];
    const weekStart = startOfWeek(firstDay, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(lastDay, { weekStartsOn: 1 });

    // Calculate padding
    const padLeftDays = eachDayOfInterval({
      start: weekStart,
      end: firstDay,
    });
    const padRightDays = eachDayOfInterval({
      start: lastDay,
      end: weekEnd,
    });

    // Padding is always calculated for a 7-day week structure
    // (weekend placeholders will fill in when hideWeekend is true)
    const padLeft = padLeftDays.length - 1;
    const padRight = padRightDays.length - 1;

    // Convert days to WeekGroupDay objects with isWeekend and isRedDay flags
    const weekGroupDays: WeekGroupDay[] = days.map((date) => ({
      date,
      isWeekend: isWeekend(date),
      isRedDay: isRedDay(date),
    }));

    return {
      weekNumber,
      startDate: weekStart,
      days: weekGroupDays,
      padLeft,
      padRight,
    };
  });
}
